import type { GrantablePermission, PageMeta } from '@palaccount/contracts';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import type { ListExportColumn } from '@/features/reports/export';

/**
 * THE CONFIG EVERY DOCUMENT LIST PAGE IS WRITTEN IN (wave 3, `specs/erp-gap-analysis.md`).
 *
 * One screen shape — summary tiles, a filterable list on the start side, the
 * selected document's flow beside it — was built once, by hand, for Sales
 * Orders (SAL-02) and then wanted by nine more registers. Rather than nine
 * near-copies, a page now DESCRIBES itself through this object and
 * `DocumentListPage` draws it. Two conventions keep the description honest:
 *
 *  - STATIC LABELS ARE KEYS (`labelKey`, `titleKey`…): the page component
 *    translates them, so `i18n-key-existence.test.ts` sees every one as a
 *    string literal on the configuring page.
 *  - COMPUTED LABELS ARE FUNCTIONS RETURNING TRANSLATED TEXT (`statusLabel`,
 *    an export column's `header`): the page builds its config inside render
 *    with `t` in scope, and nothing here needs an i18n instance of its own.
 *
 * Nothing in a config is memoised against; a page may rebuild it on every
 * render, closing over its own `busy` and dialogs, and the list page reads it
 * fresh each time.
 */

// ── The list read ───────────────────────────────────────────────────────────

export interface ListFilters<Status extends string> {
  /** `''` is "all statuses". */
  status: Status | '';
  /** Debounced, trimmed; `''` when the page has no search box. */
  q: string;
  /**
   * Wave 3B — the date window on the document's own date, ISO, both
   * inclusive; `''` when unset or when the page draws no date boxes.
   */
  dateFrom: string;
  dateTo: string;
  /** The picked partner's id; `''` when none is picked or the page has no partner box. */
  partnerId: string;
}

/** Wave 3B — the partner box: which side of the ledger it searches. */
export interface PartnerFilterConfig {
  kind: 'customer' | 'supplier';
}

/** A page of rows, with the names the hooks disagree on folded to `rows`. */
export interface ListPage<Row> extends PageMeta {
  rows: readonly Row[];
}

export interface ListQuery<Row> {
  data: ListPage<Row> | undefined;
  isPending: boolean;
  isError: boolean;
}

/**
 * `{ orders: Row[], total, page, pageSize }` → `{ rows, total, page, pageSize }`.
 * Every paged body names its array after its noun; the list page needs one
 * name, and this is the one place the renaming happens.
 */
export function normalisePaged<Key extends string, Row>(
  body: (PageMeta & Record<Key, readonly Row[]>) | undefined,
  key: Key,
): ListPage<Row> | undefined {
  if (body === undefined) return undefined;
  return { rows: body[key], total: body.total, page: body.page, pageSize: body.pageSize };
}

// ── Summary tiles ───────────────────────────────────────────────────────────

/**
 * `count` draws the integer; `money` the 2dp figure with its currency;
 * `moneyWhole` the whole units ("$79,692, not $79,692.00" — SAL-02 §5a);
 * `pct` a percentage figure with its sign ("37.5%"); `text` the string as
 * the page already translated it (a reason, a name).
 * The currency is EXPLICIT: a tile over the whole register is a base-currency
 * figure (GEN-107) and the page says which, never a literal sign.
 */
export type TileFormat = 'count' | 'money' | 'moneyWhole' | 'pct' | 'text';

export interface KpiTileSpec<Stats> {
  key: string;
  labelKey: string;
  format: TileFormat;
  value: (stats: Stats) => string | number;
  /**
   * Required by the two money formats; ignored by `count`. A function reads
   * it off the stats themselves — every wave-3B `/stats` body NAMES the base
   * currency its money is in, so the tile need not guess it from the session.
   */
  currency?: string | ((stats: Stats) => string) | undefined;
  tone?: 'pos' | 'neg' | 'warn' | undefined;
}

export interface KpiConfig<Stats> {
  /** The register-wide `/stats` read (PAGINATION.md §3 step 10). */
  useStats: () => { data: Stats | undefined; isPending: boolean; isError: boolean };
  tiles: readonly KpiTileSpec<Stats>[];
}

// ── The list card, and the table it becomes ────────────────────────────────

export interface DocumentCardModel {
  /** The printed document number — an LTR island. */
  code: string;
  /** The page's own status chip. */
  chip: ReactNode;
  /** Who the document is with. */
  title: string;
  /** An optional second line of the card (a date, a reference). */
  meta?: ReactNode;
  amount?: { value: string; currency: string } | undefined;
}

/** Header keys for table mode; every one has a `doclist.col*` default. */
export interface ColumnLabels {
  codeKey?: string | undefined;
  statusKey?: string | undefined;
  titleKey?: string | undefined;
  metaKey?: string | undefined;
  amountKey?: string | undefined;
}

// ── The selected document's flow ───────────────────────────────────────────

export type StepState = 'done' | 'current' | 'skipped' | 'pending';

/**
 * A stage with a document behind it LINKS (`href`); a stage with no document
 * yet that is the next sensible act IS the act (`action`, D-09) — a
 * navigation to a pre-filled editor, or the page's own handler. Either is
 * greyed with its reason when the role cannot (`permission`, BR-PERM-17/-18).
 */
export interface LifecycleStep {
  key: string;
  labelKey: string;
  state: StepState;
  href?: string | undefined;
  action?:
    | {
        /** The tooltip and accessible name — what the click will make. */
        labelKey: string;
        to?: string | undefined;
        onAct?: (() => void) | undefined;
        disabled?: boolean | undefined;
      }
    | undefined;
  permission?: { permission: GrantablePermission; reasonKey: string } | undefined;
}

export interface LinkedDoc {
  key: string;
  to: string;
  label: ReactNode;
  icon?: LucideIcon | undefined;
  /** `muted` for provenance (the quote it came from), `blue` for the document it made. */
  tone?: 'muted' | 'blue' | undefined;
}

export interface LineColumn {
  key: string;
  labelKey: string;
  align?: 'start' | 'end' | undefined;
  className?: string | undefined;
}

export interface LineRow {
  key: string;
  cells: readonly ReactNode[];
}

export interface TotalsModel {
  /** "Net / Tax / Total" — the one label ahead of the figures. */
  labelKey: string;
  amounts: readonly { value: string; currency: string; emphasis?: boolean | undefined }[];
}

export interface DetailConfig<Row> {
  chip: (row: Row) => ReactNode;
  links?: ((row: Row) => readonly LinkedDoc[]) | undefined;
  subline?: ((row: Row) => ReactNode) | undefined;
  steps?: { labelKey: string; of: (row: Row) => readonly LifecycleStep[] } | undefined;
  /** The sentence under the stepper ("60% delivered · 0% invoiced"), or null. */
  progress?: ((row: Row) => string | null) | undefined;
  /** The page's own buttons and the dialogs that belong inside their conditions. */
  actions?: ((row: Row) => ReactNode) | undefined;
  lines?: { columns: readonly LineColumn[]; rows: (row: Row) => readonly LineRow[] } | undefined;
  totals?: ((row: Row) => TotalsModel | null) | undefined;
  /**
   * The escape hatch: replaces everything BELOW the header (chip + links),
   * for a page whose detail is not a flow-and-lines panel.
   */
  render?: ((row: Row) => ReactNode) | undefined;
}

// ── Export ──────────────────────────────────────────────────────────────────

export interface ExportConfig<Row, Status extends string> {
  /** The base name — "sales-orders"; the date and extension are appended. */
  filename: string;
  columns: readonly ListExportColumn<Row>[];
  /** The imperative page read the walk uses — the same filter the screen shows. */
  fetchPage: (
    filters: ListFilters<Status>,
    page: number,
    pageSize: number,
  ) => Promise<{ rows: readonly Row[]; total: number }>;
}

// ── The whole page ──────────────────────────────────────────────────────────

export interface DocumentListConfig<Row extends { id: string }, Status extends string, Stats = unknown> {
  titleKey: string;
  /** The module's tab strip, drawn above the title row. */
  tabs?: ReactNode;
  newAction?:
    | { to: string; labelKey: string; permission: GrantablePermission; reasonKey: string }
    | undefined;
  statuses: readonly Status[];
  statusLabel: (status: Status) => string;
  /** Present → a debounced search box feeding `filters.q` (GEN-25). */
  search?: { labelKey: string; placeholderKey: string } | undefined;
  /** Wave 3B — true → the two date boxes bounding the document date (`filters.dateFrom/dateTo`). */
  dateRange?: boolean | undefined;
  /** Wave 3B — present → a partner typeahead feeding `filters.partnerId`. */
  partner?: PartnerFilterConfig | undefined;
  useList: (filters: ListFilters<Status>, page: number, pageSize: number) => ListQuery<Row>;
  /**
   * The deep-link fallback: `?selected=` names a document that is not on the
   * current page, so it is read by id. A page without one shows its
   * select-one prompt instead.
   */
  useById?: ((id: string | null) => { data: Row | undefined }) | undefined;
  kpis?: KpiConfig<Stats> | undefined;
  card: (row: Row) => DocumentCardModel;
  columns?: ColumnLabels | undefined;
  detail: DetailConfig<Row>;
  export?: ExportConfig<Row, Status> | undefined;
  /**
   * Table mode only: the slot drawn above the table while rows are ticked.
   * `clear` unticks them — call it after the act.
   */
  bulkActions?: ((rows: readonly Row[], clear: () => void) => ReactNode) | undefined;
  /** The URL parameter the selected id lives in; `selected` by default. */
  selectedParam?: string | undefined;
  /** The per-page storage key for the split/table choice; the title key by default. */
  storageKey?: string | undefined;
  /** The list's own worded empty state (GEN-31/GEN-32). */
  emptyKey: string;
  /** The detail panel's prompt while nothing is selected. */
  selectOneKey: string;
  /** Anything the page wants between the tiles and the list — its error strip. */
  notice?: ReactNode;
}
